import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { calculateDelta, formatDecimals, formatNumber, formatNumberWithCommas, formatString, formatTiers } from '../../utils';
import { Flexbox, FlexboxColumn } from '../action/Flexbox';
import Tooltip from '../action/TooltipWrapper';
import { GreenBadge, RedBadge } from '../Badge/Badge';
import Logo from '../Logo/Logo';
import ThemedText from '../theme/ThemedText';
import ThemeLogo from '../theme/ThemeLogo';
import './TableContainer.scss';
import TableSkeleton from './TableSkeleton';

const TableComponent = ({ data, name, isLoading, searchTerm }) => {
  const history = useNavigate();
  const { userAddress } = useParams();
  const [{ user }] = useGlobalState();
  const [{ protocolMapping }] = useGlobalState();
  const [sortedData, setSortedData] = useState();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleClick = name => {
    history(`/profile/${userAddress || user?.address}/projects/${name}`);
  };

  const sortData = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedArray = [...data].sort((a, b) => {
      if (+a[key] < +b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (+a[key] > +b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setSortedData(sortedArray);
    setSortConfig({ key, direction });
  };

  function handleDelta(row, key) {
    const value = Number(calculateDelta(Number(row?.rank), Number(row?.deltaRankings[key]?.rank)));
    if (value > 0) {
      return <GreenBadge text={`${value}%`} />;
    } else if (value < 0) {
      return <RedBadge text={`${value}%`} />;
    } else {
      return '-';
    }
  }

  function handleSortIcon(key) {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        return <ThemeLogo key={`${key}_asc`} icon={'sort_asc'} />;
      } else {
        return <ThemeLogo key={`${key}_desc`} icon={'sort_desc'} />;
      }
    } else {
      return <ThemeLogo key={`${key}_sort`} icon={'sort'} />;
    }
  }

  useEffect(() => {
    if (data) setSortedData(data);
  }, [data]);

  useEffect(() => {
    if (data) {
      if (!searchTerm) return setSortedData(data);
      const handler = setTimeout(() => {
        const filtered = data?.filter(protocol => protocol?.platform?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
        setSortedData(filtered);
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [searchTerm, data]);

  if (isLoading) return <TableSkeleton rows={4} />;

  return (
    <FlexboxColumn className='table_container' gap='0'>
      <Flexbox className='table_container__header' fw>
        <div className='name'>
          <ThemedText.P weight='semi-bold'>{name}</ThemedText.P>
        </div>
        <Flexbox className='contribution pointer' justifyContent='flex-start' gap='0.5rem' onClick={() => sortData('rank')}>
          <ThemedText.P weight='semi-bold'>Contribution Rank</ThemedText.P>
          <Tooltip
            content={'Contribution Rank is calculated by considering your volume in this project across the tracked chains, and compared with other users in the same environment.'}
            position={'right'}
          >
            <ThemeLogo icon={'info'} />
          </Tooltip>
          {handleSortIcon('rank')}
        </Flexbox>
        <Flexbox className='rank pointer' justifyContent='flex-start' gap='0.5rem' onClick={() => sortData('volume')}>
          <ThemedText.P weight='semi-bold'>Volume</ThemedText.P>
          {handleSortIcon('volume')}
        </Flexbox>

        <Flexbox className='rank' justifyContent='space-between' gap='0.5rem' onClick={() => sortData('volume')}>
          <ThemedText.P weight='semi-bold'>Rank Δ 7D</ThemedText.P>
          {/* <ThemeLogo icon={sortConfig?.key === 'volume' ? `sort-${sortConfig?.direction}` : 'sort'} /> */}
        </Flexbox>
        <Flexbox className='rank' justifyContent='space-between' gap='0.5rem' onClick={() => sortData('volume')}>
          <ThemedText.P weight='semi-bold'>Rank Δ 30D</ThemedText.P>
          {/* <ThemeLogo icon='sort' /> */}
        </Flexbox>
        <div className='action'></div>
      </Flexbox>
      {sortedData && sortedData.length === 0 && (
        <Flexbox className='table_container__row' fw justifyContent='center'>
          <ThemedText.H5>No data found </ThemedText.H5>
        </Flexbox>
      )}
      {sortedData &&
        sortedData.map((row, index) => (
          <Flexbox className='table_container__row' fw key={index} onClick={() => handleClick(row?.platform)}>
            <Flexbox className='name' gap='0.5rem' justifyContent='flex-start'>
              <Logo width='1.25rem' height='1.25rem' borderRadius='50%' src={protocolMapping[row?.platform]?.logo} />
              <ThemedText.P>{formatString(row?.platform)}</ThemedText.P>
            </Flexbox>
            <div className='contribution'>
              <ThemedText.P>
                {formatNumberWithCommas(row?.rank)} {formatTiers(row?.top) && <ThemedText.Span weight='semi-bold'> (Top {formatTiers(row?.top)}%) </ThemedText.Span>}
              </ThemedText.P>
            </div>
            <div className='rank'>
              <ThemedText.P>${formatNumber(formatDecimals(row?.volume))}</ThemedText.P>
            </div>

            <Flexbox className='rank' justifyContent='flex-start' gap='0.25rem'>
              <ThemedText.P>{handleDelta(row, '7d')}</ThemedText.P>
            </Flexbox>
            <Flexbox className='rank' justifyContent='flex-start' gap='0.25rem'>
              <ThemedText.P>{handleDelta(row, '30d')}</ThemedText.P>
            </Flexbox>
            <div className='action' onClick={() => handleClick(row?.platform)}>
              →
            </div>
          </Flexbox>
        ))}
    </FlexboxColumn>
  );
};

export default TableComponent;
